var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
/**
 * One pending patient self-booking as the practice inbox renders it (PRD #1343,
 * RS-002). A read-only projection composed by the patient-booking BFF from the
 * held appointment plus its linked treatment plan — the practice sees who
 * booked, against which plan/Heilmittel, when, and (for first-contact bookings)
 * the intake payload it must vet before confirming.
 *
 * Plan-backed holds carry `treatmentPlanId` + `treatmentName`; first-contact
 * holds (later slices) carry `intakePayload` instead. Both always carry the
 * patient, the therapist the hold is blocking, and the slot time.
 */
export class PendingHoldInboxItemDto {
    /** The held appointment's id — the confirm/decline target. */
    appointmentId;
    /**
     * Groups the holds of one multi-slot pass (PRD #1343, RS-004). The inbox
     * groups items by this id and offers batch confirm/decline across a pass;
     * null for single-slot holds, which the inbox renders on their own.
     */
    bookingPassId;
    /**
     * The patient's user id. The first-contact confirm handoff (RS-008) prefills
     * the treatment-plan form with this patient before linking the hold.
     */
    patientId;
    patientName;
    scheduledStart;
    plannedDuration;
    /** Display name of the therapist this hold is currently blocking. */
    therapistName;
    /**
     * True when the patient chose "egal" (RS-007): the therapist above was
     * auto-preselected so the hold could block a concrete calendar, and the
     * practice may reassign it at confirm. The inbox shows the marker.
     */
    therapistPreferenceEgal;
    /** Set for plan-backed holds; null for first-contact holds. */
    treatmentPlanId;
    /** The plan's Heilmittel display name, when plan-backed. */
    treatmentName;
    /**
     * The patient-entered intake snapshot for first-contact holds (Heilmittel,
     * counts, frequency, urgency). Null for plan-backed holds, which are vetted
     * already. Rendered read-only in the inbox.
     */
    intakePayload;
    /**
     * True when the hold originated from the public website widget (PRD #1358,
     * RS-004). The inbox badges it so the front desk knows this is a first
     * contact from a stranger, not an existing patient's in-app self-booking.
     * Only VERIFIED website holds reach the inbox, so a badged item is always
     * email-confirmed.
     */
    fromWebsite;
    /**
     * The website visitor's contact — email + phone off the booking's intake
     * payload (never a person row), so the practice can call back (the phone is
     * collected, not verified). Null for non-website holds.
     */
    websiteContactEmail;
    websiteContactPhone;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], PendingHoldInboxItemDto.prototype, "appointmentId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], PendingHoldInboxItemDto.prototype, "bookingPassId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], PendingHoldInboxItemDto.prototype, "patientId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], PendingHoldInboxItemDto.prototype, "patientName", void 0);
__decorate([
    Expose(),
    Type(() => Date),
    __metadata("design:type", Date)
], PendingHoldInboxItemDto.prototype, "scheduledStart", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], PendingHoldInboxItemDto.prototype, "plannedDuration", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], PendingHoldInboxItemDto.prototype, "therapistName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], PendingHoldInboxItemDto.prototype, "therapistPreferenceEgal", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], PendingHoldInboxItemDto.prototype, "treatmentPlanId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], PendingHoldInboxItemDto.prototype, "treatmentName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], PendingHoldInboxItemDto.prototype, "intakePayload", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], PendingHoldInboxItemDto.prototype, "fromWebsite", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], PendingHoldInboxItemDto.prototype, "websiteContactEmail", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], PendingHoldInboxItemDto.prototype, "websiteContactPhone", void 0);
//# sourceMappingURL=pending-hold-inbox-item.dto.js.map